/**
 * スクロールに応じた出現演出
 *
 * ファーストビューの要素は HTML に data-reveal を書いておく。
 * （JS の前に .has-reveal が付くので、最初から隠れた状態で描画できる）
 * それ以外はここで対象を集めて data-reveal を付ける。
 * 並び順に応じて --i を持たせ、遅延はCSS側で組み立てる。
 *
 * 動きを減らす設定の場合はルートにクラスを付けないので、
 * CSS側の初期状態（透明）も適用されず、そのまま表示される。
 */

const PREFERS_REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// 出現の単位。親が対象になった場合、その子孫は対象から外す。
const TARGETS = [".hero .lead > *", ".sec > .inner > .title", ".sec > .inner > .chart", ".sec > .inner > .btn-wrapper", ".anchor > *", ".cards > *", ".schedule-list > *", ".sponsor .list > *"];

let observer = null;
const initialized = new WeakSet();

const getObserver = () => {
	if (observer) return observer;

	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				entry.target.classList.add("is-in");
				observer.unobserve(entry.target);
			});
		},
		{
			root: null,
			// 少し画面に入ってから動き出す
			rootMargin: "0px 0px -10% 0px",
			threshold: 0,
		},
	);

	return observer;
};

/**
 * 対象を集めて、入れ子になっているものを間引く
 * @param {Element|Document} scope
 * @returns {Element[]}
 */
const collect = (scope) => {
	const found = [...scope.querySelectorAll(TARGETS.join(","))];
	return found.filter((el) => !found.some((other) => other !== el && other.contains(el)));
};

/**
 * @param {Element|Document} scope - 探索範囲
 */
export const initReveal = (scope = document) => {
	if (PREFERS_REDUCED_MOTION) return;

	document.documentElement.classList.add("has-reveal");

	const io = getObserver();
	const counters = new Map();
	const added = [];

	collect(scope).forEach((node) => {
		if (initialized.has(node)) return;
		initialized.add(node);

		const el = node;

		// 同じ親の中での並び順を遅延に使う
		const parent = el.parentElement;
		const index = counters.get(parent) || 0;
		counters.set(parent, index + 1);
		el.style.setProperty("--i", index);

		// HTMLにあらかじめ書いてある分は、すでに隠れた状態で描画されている
		if (el.hasAttribute("data-reveal")) {
			io.observe(el);
			return;
		}

		// 付与した瞬間に遷移させない。
		// htmxで後から挿入される要素が一度見えてから消えるのを防ぐ。
		el.style.transition = "none";
		el.setAttribute("data-reveal", "");
		added.push(el);
	});

	if (added.length === 0) return;

	// 一度レイアウトを走らせて「透明・遷移なし」の状態を確定させる。
	// これをしないと、後から挿入された要素が見えた状態から消えていく。
	document.documentElement.getBoundingClientRect();

	added.forEach((el) => {
		el.style.removeProperty("transition");
		io.observe(el);
	});
};

export default initReveal;
